/**
 * Language handling for the frontend.
 *
 * Mirrors `shared/search_shared/languages.py`. SUPPORTED_LANGUAGES must stay in
 * sync with it: those are the languages the search index has analyzers for, and
 * sending anything else as `user_language` silently degrades a query to the
 * unstemmed catchall fields.
 *
 * Content language and UI language are deliberately separate. Content language
 * is what the backend scores against and covers all nine analyzed languages; UI
 * language only covers the locale bundles we actually ship. A French user gets
 * French content with an English interface, which is correct and costs nothing.
 */

/** Languages the search index has analyzers for. Keep in sync with the backend. */
export const SUPPORTED_LANGUAGES = ['en', 'de', 'it', 'fr', 'es', 'pt', 'pl', 'ja', 'ko'] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/** Languages with a translation bundle. A subset of SUPPORTED_LANGUAGES. */
export const UI_LANGUAGES = ['en', 'de'] as const;

export type UiLanguage = (typeof UI_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

/**
 * Reduce a language tag to a supported language, or null.
 *
 * `navigator.language` yields regional tags such as `en-US`, `pt-BR` or `de-AT`,
 * none of which match an analyzer name on their own.
 */
export function normalizeLanguage(code: string | null | undefined): SupportedLanguage | null {
  if (!code) return null;
  const base = code.split('-')[0].toLowerCase();
  return SUPPORTED_LANGUAGES.includes(base as SupportedLanguage)
    ? (base as SupportedLanguage)
    : null;
}

/**
 * Reduce a language tag to a language we have a translation bundle for, or null.
 */
export function normalizeUiLanguage(code: string | null | undefined): UiLanguage | null {
  if (!code) return null;
  const base = code.split('-')[0].toLowerCase();
  return UI_LANGUAGES.includes(base as UiLanguage) ? (base as UiLanguage) : null;
}

/** Best supported language for the current browser, falling back to English. */
export function detectBrowserLanguage(): SupportedLanguage {
  const languages = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const candidate of languages) {
    const normalized = normalizeLanguage(candidate);
    if (normalized) return normalized;
  }
  return DEFAULT_LANGUAGE;
}
